import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { formatFilterLabel, getCounterValue } from '../../../libs/filters'
import { CartFilters, PlpFilters, ToggleFilters } from '../../../model/filters'
import { palette, pxToRem } from '../../../style/theme'
import Checkbox from '../../../styleguide/Checkbox'
import { scrollbarCss } from '../../CommonComponents'
import ChevronDownIcon from '../../icons/ChevronDownIcon'
import ChevronRightIcon from '../../icons/ChevronRightIcon'
import BaseFilter from '../BaseFilter/BaseFilter'

const plpFilterNames = <T extends readonly (keyof PlpFilters)[]>(names: T) => names

const OPTIMATCH_FILTER_NAMES = plpFilterNames([
  'myopia',
  'hyperopia',
  'astigmatism',
  'presbyopia',
] as const)

type OptiMatchFilterName = typeof OPTIMATCH_FILTER_NAMES[number]

// Space kept on the right so the panel never touches the RELEASE column next to it.
const COLUMN_GAP_VW = 3

// The whole component sits outside the flex flow (absolute, anchored right below
// its trigger point in the parent column) so that widening it — either the header
// alone when open, or the expanded content — never pushes the RELEASE / FRONT
// COLOR columns to the side. The parent must be `position: relative`.
const Wrapper = styled.div<{ width: number }>`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  width: ${({ width }) => width - COLUMN_GAP_VW}vw;
  padding-bottom: ${pxToRem(80)}rem;
`

const RootHeader = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0.6em 1em;
  color: ${palette.biscay};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  text-transform: uppercase;
  background: ${palette.wildSand};
  border: 1px solid ${palette.cornflowerBlue};
  border-radius: ${({ open }) =>
    open ? `${pxToRem(4)}rem ${pxToRem(4)}rem 0 0` : `${pxToRem(4)}rem`};
`

const RootChevron = styled(ChevronDownIcon)<{ open: boolean }>`
  width: 1.4em;
  height: 1.4em;
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'none')};
`

// Capped + internally scrollable: it can grow arbitrarily tall (up to 4
// conditions, each with up to 4 severities, expanded at once), so it must
// not rely on the surrounding modal's own scroll to stay fully reachable.
const OptiMatchList = styled.div`
  max-height: 90vh;
  overflow-y: auto;
  background: ${palette.white};
  border: 1px solid ${palette.cornflowerBlue};
  border-top: none;
  border-radius: 0 0 ${pxToRem(4)}rem ${pxToRem(4)}rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0 1em 0.6em;
  ${scrollbarCss}
`

const ConditionRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5em 0;
  border-top: 1px solid ${palette.alto};
`

const CheckboxContainer = styled.div`
  margin-right: 0.6em;
  display: flex;

  label {
    margin: 0;
  }
`

const ConditionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  cursor: pointer;
  color: ${palette.biscay};
`

const ToggleChevron = styled(ChevronRightIcon)<{ open: boolean }>`
  width: 1.4em;
  height: 1.4em;
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? 'rotate(90deg)' : 'none')};
`

const SeverityList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.6em 0 0.2em 1em;
`

type OptiMatchFiltersProps = {
  width: number
  expandedWidth?: number
  filters: CartFilters
  toggleSelectedFilters: ToggleFilters
}

const OptiMatchFilters: React.FC<OptiMatchFiltersProps> = ({
  width,
  expandedWidth = 16,
  filters,
  toggleSelectedFilters,
}) => {
  const { t } = useTranslation()

  const [isRootOpen, setIsRootOpen] = useState(false)
  const [openConditions, setOpenConditions] = useState<OptiMatchFilterName[]>([])

  const toggleCondition = (filterName: OptiMatchFilterName) =>
    setOpenConditions(current =>
      current.includes(filterName)
        ? current.filter(name => name !== filterName)
        : [...current, filterName],
    )

  return (
    <Wrapper width={isRootOpen ? expandedWidth : width}>
      <RootHeader open={isRootOpen} onClick={() => setIsRootOpen(open => !open)}>
        {t('Filters.item_optimatch')}
        <RootChevron open={isRootOpen} />
      </RootHeader>

      {isRootOpen && (
        <OptiMatchList>
          {OPTIMATCH_FILTER_NAMES.map(filterName => {
            const filter = filters[filterName]
            const allOptionIds = filter.options.map(({ id }) => id)
            const selectedOptionIds = allOptionIds.filter(id => filter.selected.includes(id))
            const selectableOptionIds = allOptionIds.filter(id => getCounterValue(filter, id) > 0)
            const selectedCount = selectedOptionIds.reduce(
              (total, id) => total + getCounterValue(filter, id),
              0,
            )
            const isSomeSelected = selectedCount > 0

            const isAllSelected = allOptionIds.length > 0 && selectedCount === allOptionIds.length

            const isConditionOpen = openConditions.includes(filterName)
            const areAllSeveritiesDisabled = selectableOptionIds.length === 0

            return (
              <React.Fragment key={filterName}>
                <ConditionRow>
                  <CheckboxContainer>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isSomeSelected && !isAllSelected}
                      disabled={areAllSeveritiesDisabled}
                      onChange={() =>
                        isAllSelected
                          ? toggleSelectedFilters(filterName, allOptionIds, 'checkbox', true)
                          : toggleSelectedFilters(
                              filterName,
                              selectableOptionIds.filter(id => !filter.selected.includes(id)),
                              'checkbox',
                              false,
                            )
                      }
                    />
                  </CheckboxContainer>
                  <ConditionLabel onClick={() => toggleCondition(filterName)}>
                    <span>
                      {t(`Filters.item_${formatFilterLabel(filterName)}`)}
                      {selectedCount > 0 ? ` (${selectedCount})` : ''}
                    </span>
                    <ToggleChevron open={isConditionOpen} />
                  </ConditionLabel>
                </ConditionRow>
                {isConditionOpen && (
                  <SeverityList>
                    {filter.options.map((optionObject, idx) => (
                      <BaseFilter
                        key={idx}
                        toggle={() =>
                          toggleSelectedFilters(filterName, optionObject.id, 'checkbox')
                        }
                        quantity={getCounterValue(filter, optionObject.id)}
                        type="checkbox"
                        optionLabel={optionObject.label}
                        optionName={optionObject.name}
                        filterName={filterName}
                        value={optionObject.id}
                        checked={filter.selected.includes(optionObject.id)}
                      />
                    ))}
                  </SeverityList>
                )}
              </React.Fragment>
            )
          })}
        </OptiMatchList>
      )}
    </Wrapper>
  )
}

export default OptiMatchFilters
